// RUTA: src/lib/captureWithMessage.ts
//
// Helper para capturar un elemento DOM + un mensaje de texto en una sola
// imagen PNG. El texto se compone DEBAJO del elemento capturado.
//
// Por qué esto: en iOS, cuando se comparte `{ files, text }` a WhatsApp,
// el texto se mete en un campo de "caption" oculto detrás del preview
// gris. Si el cliente no toca el preview antes de mandar, WhatsApp
// descarta el texto y solo envía la foto. Embebiendo el texto en la
// imagen, garantizamos que toda la info viaja siempre como un solo PNG
// sin depender del comportamiento de WhatsApp.

import html2canvas from 'html2canvas';

/**
 * Captura `element` con html2canvas, luego compone un PNG que contiene
 * el screenshot original + el `message` renderizado debajo. Devuelve el
 * Blob listo para `navigator.share` o clipboard.
 */
export async function captureWithMessage(
  element: HTMLElement,
  message: string,
): Promise<Blob | null> {
  // 1. Capturar el elemento original
  const origCanvas = await html2canvas(element, {
    backgroundColor: '#0a0a0a',
    scale: 2,
    logging: false,
    useCORS: true,
  });

  // 2. Renderizar el mensaje en un div fuera de viewport y capturarlo.
  //    Usamos html2canvas para que respete emojis y CSS — fillText nativo
  //    no siempre renderiza emojis a color en todos los browsers.
  const msgDiv = document.createElement('div');
  // Ancho del mensaje = ancho del canvas original (en pixels CSS, no
  // pixels físicos del canvas; html2canvas con scale=2 duplica el width
  // del canvas, así que lo dividimos para que coincida visualmente).
  const cssWidth = Math.round(origCanvas.width / 2);
  msgDiv.style.cssText = `
    position: fixed;
    top: -10000px;
    left: 0;
    width: ${cssWidth}px;
    padding: 18px 22px;
    color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Apple Color Emoji", "Segoe UI Emoji", sans-serif;
    font-size: 14px;
    line-height: 1.65;
    white-space: pre-line;
    background: #0a0a0a;
    box-sizing: border-box;
    border-top: 1px solid #1f1f1f;
  `;
  msgDiv.textContent = message;
  document.body.appendChild(msgDiv);

  let msgCanvas: HTMLCanvasElement;
  try {
    msgCanvas = await html2canvas(msgDiv, {
      backgroundColor: '#0a0a0a',
      scale: 2,
      logging: false,
    });
  } finally {
    document.body.removeChild(msgDiv);
  }

  // 3. Componer ambos canvases en uno: original arriba, mensaje abajo.
  const combined = document.createElement('canvas');
  combined.width = Math.max(origCanvas.width, msgCanvas.width);
  combined.height = origCanvas.height + msgCanvas.height;
  const ctx = combined.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, combined.width, combined.height);
  // Centrar horizontalmente por si los anchos difieren ligeramente
  ctx.drawImage(origCanvas, Math.round((combined.width - origCanvas.width) / 2), 0);
  ctx.drawImage(msgCanvas, Math.round((combined.width - msgCanvas.width) / 2), origCanvas.height);

  return await new Promise<Blob | null>(resolve =>
    combined.toBlob(blob => resolve(blob), 'image/png', 1.0),
  );
}
